import {AuthorizedRequestBuilder} from "utils/sagaBuilder";
import {all, call, put, select, takeLatest} from "redux-saga/effects";
import {authorizedRequest, HTTP_METHODS} from "utils/request";
import {generic} from "utils/action";
import {
  closeMessageError,
  closeMessageSuccess,
  getMessageDetailsError,
  getMessageDetailsSuccess,
  getMessagesError,
  getMessagesSuccess,
  getTreatmentHistoryAction,
  refittingTreatmentError,
  refittingTreatmentSuccess,
  sendRemakeRequestError,
  sendRemakeRequestSuccess,
} from "./actions";
import {
  CLOSE_MESSAGE,
  CLOSE_PRODUCTPLAN,
  CLOSE_PRODUCTPLAN_FAILED,
  CLOSE_PRODUCTPLAN_SUCCESS,
  CLOSE_PRODUCTPLAN_URL,
  CLOSE_TREATMENTPLAN,
  CLOSE_TREATMENTPLAN_FAILED,
  CLOSE_TREATMENTPLAN_SUCCESS,
  CLOSE_TREATMENTPLAN_URL,
  DELETE_PATIENT,
  DELETE_PATIENT_FAILED,
  DELETE_PATIENT_SUCCESS,
  DELETE_PATIENT_URL,
  GET_ACTIVE_PRODUCT_PLAN_FAILED,
  GET_ACTIVE_PRODUCT_PLAN_SUCCESS,
  GET_ACTIVE_PRODUCTPLAN_URL,
  GET_MESSAGE_DETAILS,
  GET_MESSAGES,
  GET_PATIENT,
  GET_PATIENT_FAILED,
  GET_PATIENT_SUCCESS,
  GET_PLANNED_TREATMENTS,
  GET_PLANNED_TREATMENTS_FAILED,
  GET_PLANNED_TREATMENTS_SUCCESS,
  GET_PLANNED_TREATMENTS_URL,
  GET_TREATMENT_HISTORY,
  GET_TREATMENT_HISTORY_FAILED,
  GET_TREATMENT_HISTORY_SUCCESS,
  GET_TREATMENT_HISTORY_URL,
  PATIENT_URL,
  SEND_ORDER_REMAKE_REQUEST,
  SEND_TREATMENT_REFITTING_REQUEST,
  UPDATE_PATIENT,
  UPDATE_PATIENT_FAILED,
  UPDATE_PATIENT_SUCCESS,
  UPDATE_PATIENT_URL,
} from "./constants";
import mocks from "./tests/Mocks";
import {makeSelectPractice} from "containers/PracticePage/selectors";
import {makeSelectMessagesCurrentPage} from "containers/PatientDataPage/selectors";
import {GET_ACTIVE_PRODUCT_PLAN} from "../OrderTreatment/constants";

export const getPatientSaga = new AuthorizedRequestBuilder(
  GET_PATIENT_SUCCESS,
  GET_PATIENT_FAILED,
)
  .url(({ patientId }) => PATIENT_URL(patientId))
  .build();

export const updatePatientSaga = new AuthorizedRequestBuilder(
  UPDATE_PATIENT_SUCCESS,
  UPDATE_PATIENT_FAILED,
)
  .method(HTTP_METHODS.PUT)
  .body(({ patient }) => patient)
  .url(UPDATE_PATIENT_URL)
  .build();

export function* deletePatientSaga({ patient }) {
  try {
    yield call(authorizedRequest, DELETE_PATIENT_URL(), {
      body: patient,
      method: HTTP_METHODS.DELETE,
    });
    yield put(generic(DELETE_PATIENT_SUCCESS, { patient }));
  } catch (e) {
    yield put(generic(DELETE_PATIENT_FAILED, { reason: e.response.data }));
  }
}

export function* getTreatmentHistorySagaOld({ patientId }) {
  yield put(
    generic(GET_TREATMENT_HISTORY_SUCCESS, {
      result: mocks.appointmentHistory.slice(),
      patientId,
    }),
  );
}

export const getTreatmentHistorySaga = new AuthorizedRequestBuilder(
  GET_TREATMENT_HISTORY_SUCCESS,
  GET_TREATMENT_HISTORY_FAILED,
)
  .url(({ patientId }) => GET_TREATMENT_HISTORY_URL(patientId))
  .build();

export function* getPlannedTreatmentsSaga({ patientId }) {
  try {
    const response = yield call(
      authorizedRequest,
      GET_PLANNED_TREATMENTS_URL(patientId),
    );
    yield put(getTreatmentHistoryAction(patientId));
    yield put(generic(GET_PLANNED_TREATMENTS_SUCCESS, { result: response }));
  } catch (e) {
    yield put(
      generic(GET_PLANNED_TREATMENTS_FAILED, { reason: e.response.data }),
    );
  }
}

export function* getActiveProductPlanSaga({ patientId }) {
  try {
    const response = yield call(
      authorizedRequest,
      GET_ACTIVE_PRODUCTPLAN_URL(patientId),
    );
    yield put(generic(GET_ACTIVE_PRODUCT_PLAN_SUCCESS, { result: response }));
  } catch (e) {
    yield put(
      generic(GET_ACTIVE_PRODUCT_PLAN_FAILED, { reason: e.response.data }),
    );
  }
}

export const closeTreatmentPlan = new AuthorizedRequestBuilder(
  CLOSE_TREATMENTPLAN_SUCCESS,
  CLOSE_TREATMENTPLAN_FAILED,
)
  .url(({ treatmentPlanId }) => CLOSE_TREATMENTPLAN_URL(treatmentPlanId))
  .method(HTTP_METHODS.PUT)
  .build();

export const closeProductPlan = new AuthorizedRequestBuilder(
  CLOSE_PRODUCTPLAN_SUCCESS,
  CLOSE_PRODUCTPLAN_FAILED,
)
  .url(({ productPlanId }) => CLOSE_PRODUCTPLAN_URL(productPlanId))
  .method(HTTP_METHODS.PUT)
  .build();

function* getMessageDetailsSaga({ id }) {
  try {
    const practice = yield select(makeSelectPractice());
    const response = yield authorizedRequest(
      `/practices/${practice.id}/messages/${id}`,
    );
    yield put(getMessageDetailsSuccess(response));
  } catch (error) {
    yield put(getMessageDetailsError(error));
  }
}

function* closeMessageSaga({ id }) {
  try {
    const practice = yield select(makeSelectPractice());
    const response = yield authorizedRequest(
      `/practices/${practice.id}/messages/${id}`,
      {
        method: HTTP_METHODS.PUT,
      },
    );
    yield put(closeMessageSuccess(response));
  } catch (error) {
    yield put(closeMessageError(error));
  }
}

function* getMessagesSaga({ patientId, filter = "", status = 0, limit }) {
  try {
    const practice = yield select(makeSelectPractice());
    const currentPage = yield select(makeSelectMessagesCurrentPage());
    const response = yield authorizedRequest(
      `/practices/${
        practice.id
      }/patient/${patientId}/messages/pagination/filter`,
      {
        method: HTTP_METHODS.POST,
        body: {
          filter,
          type: status,
        },
        params: {
          limit,
          offset: currentPage * limit,
        },
      },
    );
    yield put(getMessagesSuccess(response));
  } catch (error) {
    yield put(getMessagesError(error));
  }
}

function* sendRemakeRequest({ practiceId, productOrderId, remakeRequest }) {
  try {
    const response = yield authorizedRequest(
      `/practices/${practiceId}/productOrder/${productOrderId}/remake`,
      {
        method: HTTP_METHODS.POST,
        body: remakeRequest,
      },
    );
    yield put(sendRemakeRequestSuccess(response));
  } catch (error) {
    yield put(sendRemakeRequestError(error));
  }
}

function* sendRefittingRequest({ practiceId, plannedTreatmentId}) {
  try {
    const response = yield authorizedRequest(
      `/practices/${practiceId}/plannedTreatments/${plannedTreatmentId}/refitting`,
      {
        method: HTTP_METHODS.POST,
      },
    );
    yield put(refittingTreatmentSuccess(response));
  } catch (error) {
    yield put(refittingTreatmentError(error));
  }
}



// Individual exports for testing
export default function* patientDataPageSaga() {
  yield all([
    takeLatest(GET_PATIENT, getPatientSaga),
    takeLatest(UPDATE_PATIENT, updatePatientSaga),
    takeLatest(GET_TREATMENT_HISTORY, getTreatmentHistorySaga),
    takeLatest(GET_PLANNED_TREATMENTS, getPlannedTreatmentsSaga),
    takeLatest(GET_ACTIVE_PRODUCT_PLAN, getActiveProductPlanSaga),
    takeLatest(DELETE_PATIENT, deletePatientSaga),
    takeLatest(CLOSE_TREATMENTPLAN, closeTreatmentPlan),
    takeLatest(CLOSE_PRODUCTPLAN, closeProductPlan),
    takeLatest(GET_MESSAGE_DETAILS, getMessageDetailsSaga),
    takeLatest(CLOSE_MESSAGE, closeMessageSaga),
    takeLatest(GET_MESSAGES, getMessagesSaga),
    takeLatest(SEND_ORDER_REMAKE_REQUEST, sendRemakeRequest),
    takeLatest(SEND_TREATMENT_REFITTING_REQUEST, sendRefittingRequest)
  ]);
}
